import React, { useEffect, useRef, useState } from "react";
import { FaLock, FaUser, FaTimes } from "react-icons/fa";
import logo from "../assets/images/logo.png";
import bg_image from "../assets/images/eac-pic.jpg";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import apiClient from "../api/axiosInstance";
import { LoadingButton } from "../components/loadingButton";

const LOGIN_URL = "api/auth/login";

const Login = () => {
  const userRef = useRef();
  const errRef = useRef();
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [loading, setLoading] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // Focus on the username input when the component mounts
  useEffect(() => {
    userRef.current.focus();
  }, []);

  // Clear the error message when username or password changes
  useEffect(() => {
    setErrMsg("");
  }, [username, password]);

  // Check for an existing token and auto-login if found
  useEffect(() => {
    const storedToken =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");
    const storedUsername =
      localStorage.getItem("username") || sessionStorage.getItem("username");

    if (storedToken && storedUsername) {
      // Optionally, you can validate the token
      setUser({ username: storedUsername }); // Use the retrieved username
      navigate("/hr-management"); // Auto-navigate if token exists
    }
  }, []); // Runs once when the component mounts

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const username = e.target.username.value;
    const rememberMe = e.target.rememberMe.checked; // Capture "Remember Me" checkbox state

    try {
      const response = await apiClient.post(LOGIN_URL, {
        username,
        password,
      });

      const { accessToken } = response.data;

      // Store accessToken and username based on "Remember Me"
      if (rememberMe) {
        localStorage.setItem("accessToken", accessToken); // Persistent storage
        localStorage.setItem("username", username);
      } else {
        sessionStorage.setItem("accessToken", accessToken); // Session-only storage
        sessionStorage.setItem("username", username);
      }

      setUser({ username }); // Set the user state with the logged-in username
      setUsername("");
      setPassword("");
      navigate("/hr-management");
    } catch (error) {
      if (error.response) {
        setErrMsg(error.response.data.message);
      } else {
        setErrMsg(
          "Sign In failed. Please check your internet connection and try again."
        );
      }
    }
    setLoading(false);
  };

  const handleNavigateHome = () => {
    navigate("/");
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center font-inter p-6"
      style={{ backgroundImage: `url(${bg_image})` }}
    >
      <section className="relative bg-white bg-opacity-85 p-3 md:p-6 px-7 rounded-3xl shadow-md w-full max-w-[49rem] md:px-40">
        <button
          className="absolute top-4 right-6 text-red-500 hover:text-red-700 font-extrabold text-xl"
          onClick={handleNavigateHome}
        >
          <FaTimes />
        </button>

        {/* Logo */}
        <div className="text-center mb-3">
          <img
            src={logo}
            alt="Logo"
            className="mx-auto mb-2 h-[6rem] md:h-[8.5rem]"
          />
          <h2
            className="text-center font-bold text-[17px] md:text-[20px]"
            style={{
              color: "rgba(83, 83, 83, 0.9)",
              width: "100%",
            }}
          >
            East African Community Secretariat
          </h2>
          <p
            className="mt-1 font-bold text-[13px] md:text-[15px]"
            style={{
              color: "rgba(83, 83, 80, 0.9)",
              width: "100%",
            }}
          >
            Sign in to access your Account
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-2 md:space-y-3">
          {/* Error Message */}
          {errMsg && (
            <p
              ref={errRef}
              className="text-red-600 font-semibold text-[12px] text-center"
              aria-live="assertive"
            >
              {errMsg}
            </p>
          )}

          {/* Username Input */}
          <div className="relative">
            <FaUser className="absolute left-8 top-[12px] md:top-[15px] text-gray-500" />
            <input
              type="text"
              placeholder="Enter your username"
              id="username"
              ref={userRef}
              autoComplete="off"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required={true}
              className="w-full py-[10px] md:py-[11px] pl-16 border text-[13px] md:text-[14px] font-medium border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-700"
            />
          </div>

          {/* Password Input */}
          <div className="relative">
            <FaLock className="absolute left-8 top-[12px] md:top-[15px] text-gray-500" />
            {/* Password Input Field */}
            <input
              type="password"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={true}
              className="w-full text-[13px] md:text-[14px] py-[10px] md:py-[11px] pl-16 border font-medium border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-700"
            />
          </div>

          <div className="flex items-center justify-between">
            {/* Remember Me for persistent login */}
            <div className="text-left">
              <div className="flex ml-2 md:ml-6">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4"
                />
                <label className="ml-2 text-[12px] md:text-sm font-semibold text-green-700">
                  Remember Me
                </label>
              </div>
            </div>
            {/* Forgot Password */}
            <div className="text-right mb-2">
              <a
                href="#"
                className="text-blue-600 font-semibold hover:underline text-[12px] md:text-sm"
              >
                Forgot your password?
              </a>
            </div>
          </div>

          {/* Login Button */}
          <div className="text-center">
            <LoadingButton
              type="submit"
              disabled={loading}
              buttonText="Sign In"
              loadingText="Signing in...."
              isLoading={loading}
              className="w-3/5 md:w-2/5  mt-3 md:p-[10px] p-[11px] text-center font-semibold text-white text-xs md:text-sm bg-green-700 rounded-[12px] hover:bg-green-800 mx-auto block"
            />
          </div>
        </form>
      </section>
    </div>
  );
};

export default Login;
